import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Trophy, Users, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcademyBrowseTeams({ user }) {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAllTeams();
  }, []);

  const fetchAllTeams = async () => {
    try {
      const response = await axios.get(`${API}/academy/all-teams-browse`, {
        withCredentials: true
      });
      setTeams(response.data);
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  // Group teams by age group
  const groupedTeams = teams.reduce((acc, team) => {
    if (!acc[team.age_group]) {
      acc[team.age_group] = [];
    }
    acc[team.age_group].push(team);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-gradient-to-r from-yellow-600 to-amber-600 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/academy/player-dashboard">
            <Button variant="ghost" className="text-white hover:text-yellow-200">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-white">Browse All Teams</h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-white mb-2">🏆 Academy Teams & Results</h2>
          <p className="text-slate-400">Check out how other teams are performing</p>
        </div>

        {teams.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-slate-400">No teams available yet.</p>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.keys(groupedTeams).sort().map((ageGroup) => (
              <div key={ageGroup}>
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
                  <Trophy className="w-6 h-6 mr-2 text-yellow-500" />
                  {ageGroup}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {groupedTeams[ageGroup].map((team) => (
                    <Card key={team.id} className="p-6 bg-slate-900/50 border-2 border-yellow-500/30 hover:border-yellow-500/30 transition-all">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h4 className="text-2xl font-bold text-white">{team.name}</h4>
                          <p className="text-sm text-slate-400 capitalize">{team.session_type}</p>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-slate-400">
                            <Users className="w-4 h-4" />
                            <span className="text-sm font-semibold">{team.player_count} players</span>
                          </div>
                        </div>
                      </div>

                      {/* Recent Games */}
                      {team.recent_games && team.recent_games.length > 0 ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="w-4 h-4 text-emerald-400" />
                            <p className="text-sm font-semibold text-slate-300">Recent Games</p>
                          </div>
                          {team.recent_games.slice(0, 3).map((game, idx) => (
                            <div key={idx} className="p-3 bg-[#0a0f1a] rounded-lg border border-white/5">
                              <div className="flex justify-between items-center">
                                <div>
                                  <p className="text-sm font-semibold text-white">
                                    vs {game.opponent || "Opponent"}
                                  </p>
                                  <p className="text-xs text-slate-400">
                                    {new Date(game.session_date).toLocaleDateString()}
                                  </p>
                                </div>
                                {game.score_home !== null && game.score_away !== null && (
                                  <div className="text-right">
                                    <p className={`text-lg font-bold ${
                                      game.score_home > game.score_away 
                                        ? 'text-emerald-400' 
                                        : game.score_home < game.score_away
                                        ? 'text-red-400'
                                        : 'text-slate-400'
                                    }`}>
                                      {game.score_home} - {game.score_away}
                                    </p>
                                    <p className="text-xs text-slate-400">
                                      {game.score_home > game.score_away ? 'Win' : 
                                       game.score_home < game.score_away ? 'Loss' : 'Draw'}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="p-4 bg-[#0a0f1a] rounded-lg text-center">
                          <p className="text-sm text-slate-400">No recent games</p>
                        </div>
                      )}

                      <Link to={`/academy/teams/${team.id}`}>
                        <Button className="w-full mt-4 bg-yellow-600 hover:bg-yellow-700" size="sm">
                          View Full Team
                        </Button>
                      </Link>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Leaderboard Teaser */}
        <Card className="p-8 mt-12 bg-gradient-to-r from-yellow-100 to-amber-100 border-2 border-yellow-500/30 text-center">
          <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">Want to See Leaderboards?</h3>
          <p className="text-slate-300 mb-4">Top scorers, assists leaders, and team standings coming soon!</p>
          <p className="text-sm text-slate-400">Keep playing and track your stats to compete with other players</p>
        </Card>
      </div>
    </div>
  );
}
